/**
 * RepoPickerModal — list the user's GitHub repos and pick one to link
 * to the current TilePacket app.
 *
 * Used by /github-callback right after OAuth exchange. Also usable from
 * the dashboard or editor if we ever want to let people change repos
 * after the initial pick.
 */

import { StyleSheet, css } from 'aphrodite';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import type { UserRepoListItem } from '@/features/editor/workspaces/versions/api/github.api';

interface Props {
  appId: string;
  repos: UserRepoListItem[];
  loading: boolean;
  onPick: (repo: UserRepoListItem) => void;
  onCancel: () => void;
}

export default function RepoPickerModal({ repos, loading, onPick, onCancel }: Props) {
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return repos;
    return repos.filter(
      (r) =>
        r.fullName.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q),
    );
  }, [query, repos]);

  return (
    <div className={css(styles.bg)} onClick={onCancel}>
      <div className={css(styles.box)} onClick={(e) => e.stopPropagation()}>
        <div className={css(styles.header)}>
          <span style={{ fontWeight: 600 }}>Select a repository</span>
          <div style={{ flex: 1 }} />
          <button className={css(styles.smallBtn)} onClick={onCancel}>
            Skip
          </button>
        </div>
        <input
          ref={inputRef}
          className={css(styles.search)}
          placeholder="Filter repos by name or description…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className={css(styles.list)}>
          {loading && <div className={css(styles.empty)}>Loading your repos…</div>}
          {!loading && filtered.length === 0 && (
            <div className={css(styles.empty)}>
              {repos.length === 0
                ? 'No repositories visible to this OAuth grant. Make sure the app has access to at least one repo.'
                : 'No repos match your filter.'}
            </div>
          )}
          {!loading &&
            filtered.map((repo) => (
              <button key={repo.fullName} className={css(styles.row)} onClick={() => onPick(repo)}>
                <div className={css(styles.rowMain)}>
                  <span className={css(styles.rowName)}>{repo.fullName}</span>
                  {repo.private && <span className={css(styles.tag)}>private</span>}
                  <span className={css(styles.rowBranch)}>{repo.defaultBranch}</span>
                </div>
                {repo.description && <div className={css(styles.rowDesc)}>{repo.description}</div>}
              </button>
            ))}
        </div>
        <div className={css(styles.footer)}>
          Pushed time is from GitHub; sorted with most recent activity first.
        </div>
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  bg: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingTop: '10vh',
    zIndex: 9999,
  },
  box: {
    width: 560,
    maxWidth: '92%',
    maxHeight: '75vh',
    overflow: 'hidden',
    background: '#fff',
    borderRadius: 10,
    border: '1px solid #e5e7eb',
    boxShadow: '0 30px 60px rgba(0,0,0,0.25)',
    display: 'flex',
    flexDirection: 'column',
    color: '#111',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  header: {
    padding: '12px 14px',
    borderBottom: '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 14,
  },
  search: {
    margin: '10px 12px',
    padding: '8px 10px',
    fontSize: 13,
    border: '1px solid #d1d5db',
    borderRadius: 6,
    outline: 'none',
    fontFamily: 'inherit',
    color: '#111',
    ':focus': { borderColor: '#2563eb' },
  },
  list: { flex: 1, overflow: 'auto', padding: '0 6px 10px' },
  empty: { padding: '20px 14px', fontSize: 13, color: '#666', textAlign: 'center' },
  row: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 4,
    padding: '10px 12px',
    borderRadius: 6,
    border: '1px solid transparent',
    background: 'transparent',
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': { background: '#f3f4f6', borderColor: '#e5e7eb' },
  },
  rowMain: { display: 'flex', alignItems: 'center', gap: 8, width: '100%' },
  rowName: {
    fontSize: 13,
    fontWeight: 600,
    fontFamily: '"SFMono-Regular", "Menlo", "Consolas", monospace',
    flex: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowBranch: {
    fontSize: 10,
    color: '#6b7280',
    fontFamily: '"SFMono-Regular", "Menlo", "Consolas", monospace',
  },
  tag: {
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 0.3,
    textTransform: 'uppercase',
    padding: '2px 5px',
    borderRadius: 3,
    background: '#fef3c7',
    color: '#92400e',
  },
  rowDesc: { fontSize: 11, color: '#6b7280', lineHeight: 1.4 },
  smallBtn: {
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#111',
    fontSize: 11,
    padding: '4px 10px',
    borderRadius: 6,
    cursor: 'pointer',
    fontFamily: 'inherit',
    ':hover': { background: '#f3f4f6' },
  },
  footer: {
    padding: '8px 14px',
    borderTop: '1px solid #e5e7eb',
    fontSize: 10,
    color: '#9ca3af',
  },
});
